import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Theme, ToastMessage, MarketScanner, EnhancedAnalysis, SubscriptionTierType } from '../types';

// Theme Store
interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'system',
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleTheme: () => {
        const current = get().theme;
        const next = current === 'dark' ? 'light' : 'dark';
        set({ theme: next });
        applyTheme(next);
      },
    }),
    { name: 'sentimentedge-theme' }
  )
);

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', isDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

// Auth Store
interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateSubscription: (tier: SubscriptionTierType, status: User['subscription_status']) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),
      login: (user, token) => set({ user, token, isAuthenticated: true }),
      logout: () => set({ user: null, token: null, isAuthenticated: false }),
      setLoading: (isLoading) => set({ isLoading }),
      updateSubscription: (tier, status) => set((state) => ({
        user: state.user ? { ...state.user, subscription_tier: tier, subscription_status: status } : null
      })),
    }),
    { name: 'sentimentedge-auth' }
  )
);

// Sidebar Store
interface SidebarStore {
  isExpanded: boolean;
  isMobileOpen: boolean;
  activeItem: string;
  toggleSidebar: () => void;
  setExpanded: (expanded: boolean) => void;
  setActiveItem: (item: string) => void;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
}

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      isExpanded: true,
      isMobileOpen: false,
      activeItem: 'dashboard',
      toggleSidebar: () => set((state) => ({ isExpanded: !state.isExpanded })),
      setExpanded: (isExpanded) => set({ isExpanded }),
      setActiveItem: (activeItem) => set({ activeItem }),
      openMobile: () => set({ isMobileOpen: true }),
      closeMobile: () => set({ isMobileOpen: false }),
      toggleMobile: () => set((state) => ({ isMobileOpen: !state.isMobileOpen })),
    }),
    {
      name: 'sentimentedge-sidebar',
      // Don't persist isMobileOpen - it should always start closed
      partialize: (state) => ({
        isExpanded: state.isExpanded,
        activeItem: state.activeItem,
      }),
    }
  )
);

// Toast Store
interface ToastStore {
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2, 9);
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    // Auto-remove after duration
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, toast.duration || 5000);
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
  clearToasts: () => set({ toasts: [] }),
}));

// User Watchlist type (from backend)
export interface UserWatchlistData {
  id: number;
  name: string;
  tickers: string[];
  is_default: boolean;
}

// Market Data Store
interface MarketStore {
  scannerData: MarketScanner | null;
  selectedAnalysis: EnhancedAnalysis | null;
  watchlist: string[];
  isScanning: boolean;
  lastScanTime: string | null;
  watchlistLoading: boolean;
  setScannerData: (data: MarketScanner | null) => void;
  setSelectedAnalysis: (analysis: EnhancedAnalysis | null) => void;
  // Local watchlist actions (now synced with backend via useWatchlist hook)
  addToWatchlist: (ticker: string) => void;
  removeFromWatchlist: (ticker: string) => void;
  setWatchlist: (tickers: string[]) => void;
  setScanning: (scanning: boolean) => void;
  setLastScanTime: (time: string | null) => void;
  setWatchlistLoading: (loading: boolean) => void;
  // User watchlists (backend-synced for paid users)
  userWatchlists: UserWatchlistData[];
  activeWatchlistId: number | null;
  setUserWatchlists: (watchlists: UserWatchlistData[]) => void;
  setActiveWatchlist: (id: number | null) => void;
  // Sync local watchlist from user's default watchlist
  syncWatchlistFromBackend: () => void;
}

export const useMarketStore = create<MarketStore>()(
  persist(
    (set, get) => ({
      scannerData: null,
      selectedAnalysis: null,
      watchlist: [],
      isScanning: false,
      lastScanTime: null,
      watchlistLoading: false,
      userWatchlists: [],
      activeWatchlistId: null,
      setScannerData: (scannerData) => set({ scannerData }),
      setSelectedAnalysis: (selectedAnalysis) => set({ selectedAnalysis }),
      addToWatchlist: (ticker) =>
        set((state) => ({
          watchlist: state.watchlist.includes(ticker)
            ? state.watchlist
            : [...state.watchlist, ticker],
        })),
      removeFromWatchlist: (ticker) =>
        set((state) => ({
          watchlist: state.watchlist.filter((t) => t !== ticker),
        })),
      setWatchlist: (tickers) => set({ watchlist: tickers }),
      setScanning: (isScanning) => set({ isScanning }),
      setLastScanTime: (lastScanTime) => set({ lastScanTime }),
      setWatchlistLoading: (watchlistLoading) => set({ watchlistLoading }),
      setUserWatchlists: (userWatchlists) => set({ userWatchlists }),
      setActiveWatchlist: (activeWatchlistId) => set({ activeWatchlistId }),
      // Sync local watchlist from user's default watchlist
      syncWatchlistFromBackend: () => {
        const { userWatchlists } = get();
        const defaultWatchlist = userWatchlists.find(w => w.is_default);
        if (defaultWatchlist) {
          set({ watchlist: defaultWatchlist.tickers, activeWatchlistId: defaultWatchlist.id });
        } else if (userWatchlists.length > 0) {
          // Use first watchlist if no default
          set({ watchlist: userWatchlists[0].tickers, activeWatchlistId: userWatchlists[0].id });
        }
      },
    }),
    {
      name: 'sentimentedge-market',
      // Don't persist isScanning or watchlistLoading - they should always start as false
      partialize: (state) => ({
        scannerData: state.scannerData,
        selectedAnalysis: state.selectedAnalysis,
        watchlist: state.watchlist,
        lastScanTime: state.lastScanTime,
        userWatchlists: state.userWatchlists,
        activeWatchlistId: state.activeWatchlistId,
      }),
    }
  )
);

// Chat Store - shared between widget and page
export interface ToolActivity {
  name: string;
  arguments?: Record<string, any>;
  status: 'calling' | 'done';
  displayName: string;
}

export interface ChatChartData {
  chartType: 'sentiment_history' | 'price_history' | 'earnings' | 'revenue' | 'cash_flow' | 'margins' | 'eps_growth' | 'share_dilution' | 'shares_outstanding' | 'fcf_per_share';
  title: string;
  symbol: string;
  data: Record<string, any>[];
  quarterlyData?: Record<string, any>[];
  balanceSheetData?: Record<string, any>[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolActivity?: ToolActivity[];
  chartData?: ChatChartData[];
}

export interface ChatConversation {
  id: number;
  title: string;
  created_at: string;
  updated_at?: string;
  messages: ChatMessage[];
}

interface ChatStore {
  // State
  conversations: ChatConversation[];
  activeConversationId: number | null;
  messages: ChatMessage[];
  isLoading: boolean;
  isOpen: boolean;

  // Actions
  setConversations: (conversations: ChatConversation[]) => void;
  setActiveConversation: (id: number | null) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateMessage: (id: string, content: string) => void;
  addToolActivity: (messageId: string, activity: ToolActivity) => void;
  addChartData: (messageId: string, chart: ChatChartData) => void;
  setLoading: (loading: boolean) => void;
  setOpen: (open: boolean) => void;
  clearMessages: () => void;
  reset: () => void;
}

// Settings Panel Store
interface SettingsPanelStore {
  isSettingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  toggleSettings: () => void;
}

export const useSettingsPanelStore = create<SettingsPanelStore>((set) => ({
  isSettingsOpen: false,
  openSettings: () => set({ isSettingsOpen: true }),
  closeSettings: () => set({ isSettingsOpen: false }),
  toggleSettings: () => set((state) => ({ isSettingsOpen: !state.isSettingsOpen })),
}));

export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      conversations: [],
      activeConversationId: null,
      messages: [],
      isLoading: false,
      isOpen: false,

      setConversations: (conversations) => set({ conversations }),

      setActiveConversation: (id) => {
        set({ activeConversationId: id });
      },

      setMessages: (messages) => set({ messages }),

      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message]
      })),

      updateMessage: (id, content) => set((state) => ({
        messages: state.messages.map(m =>
          m.id === id ? { ...m, content } : m
        )
      })),

      addToolActivity: (messageId, activity) => set((state) => ({
        messages: state.messages.map(m => {
          if (m.id !== messageId) return m;
          const existing = m.toolActivity || [];
          // Update existing entry if same tool name and status is 'done', otherwise append
          const idx = existing.findIndex(t => t.name === activity.name && t.status === 'calling');
          if (activity.status === 'done' && idx !== -1) {
            const updated = [...existing];
            updated[idx] = activity;
            return { ...m, toolActivity: updated };
          }
          return { ...m, toolActivity: [...existing, activity] };
        })
      })),

      addChartData: (messageId, chart) => set((state) => ({
        messages: state.messages.map(m => {
          if (m.id !== messageId) return m;
          return { ...m, chartData: [...(m.chartData || []), chart] };
        })
      })),

      setLoading: (isLoading) => set({ isLoading }),

      setOpen: (isOpen) => set({ isOpen }),

      clearMessages: () => set({ messages: [], activeConversationId: null }),

      reset: () => set({
        conversations: [],
        activeConversationId: null,
        messages: [],
        isLoading: false
      })
    }),
    {
      name: 'sentimentedge-chat',
      partialize: (state) => ({
        activeConversationId: state.activeConversationId,
        // Don't persist messages - load from server
      }),
    }
  )
);
